import { createClient } from '@supabase/supabase-js';
// for privy integration
import type { SupabaseClientOptions } from '@supabase/supabase-js';
import { browser } from '$app/environment';
import { isJWTExpired, decodeJWTPayload } from '$lib/utils/jwtUtils.client';

type PostTable = 'posts';
type ProfileTable = 'profiles';
type LocationTable = 'locations';
type AvatarStorageBucket = 'avatars';
type ImageStorageBucket = 'images';
type ChatStorageBucket = 'chats';
type DmStorageBucket = 'dm_images';
type OfferStorageBucket = 'offers';
type MapIconsStorageBucket = 'map-icons';
type ChatTable = 'chats';
type CommentTable = 'comments';
type DigTable = 'digs';
type NotificationTable = 'notifications';
type DmTable = 'dms';
type MessageTable = 'messages';
type MessageStatusTable = 'message_status';
type DmMembersTable = 'dm_members';
type FeatureSuggestionTable = 'feature_suggestions';
type FeatureVoteTable = 'feature_votes';
type OfferTable = 'flo_offers';
type OfferPurchaseTable = 'offer_purchases';
type OfferRatingTable = 'offer_ratings';
// for privy integration
export const cookieName = 'sb-access-token';

export const supabaseClient = createClient(
	String(import.meta.env.VITE_SUPABASE_URL),
	String(import.meta.env.VITE_SUPABASE_ANON_KEY)
);

export const setSupabaseAuthJWT = async (jwt: string | null, sessionData?: any) => {
	if (browser) {
		if (jwt) {
			// Validate JWT before using it
			if (isJWTExpired(jwt)) {
				localStorage.removeItem('supabase_jwt');
				// Clear auth header
				(supabaseClient as any).rest.headers.Authorization = null;
				return;
			}

			try {
				// For custom JWT system, just set the Authorization header
				// Don't call Supabase's auth methods
				(supabaseClient as any).rest.headers.Authorization = `Bearer ${jwt}`;
				localStorage.setItem('supabase_jwt', jwt);
			} catch (error) {
				console.error('Error setting Supabase JWT:', error);
				// Clear invalid JWT
				localStorage.removeItem('supabase_jwt');
				(supabaseClient as any).rest.headers.Authorization = null;
			}
		} else {
			localStorage.removeItem('supabase_jwt');
			// Clear auth header
			(supabaseClient as any).rest.headers.Authorization = null;
		}
	} else {
		// SSR: just set the header, don't touch localStorage
		(supabaseClient as any).rest.headers.Authorization = jwt ? `Bearer ${jwt}` : null;
	}
};

export const restoreSupabaseJWT = async () => {
	if (browser) {
		const jwt = localStorage.getItem('supabase_jwt');
		if (jwt) {
			// Check if JWT is expired or invalid before restoring
			const payload = decodeJWTPayload(jwt);
			if (isJWTExpired(jwt) || !payload) {
				localStorage.removeItem('supabase_jwt');
				// Clear auth header
				(supabaseClient as any).rest.headers.Authorization = null;
				return;
			}

			// For custom JWT system, just set the Authorization header
			// Don't call Supabase's auth methods
			(supabaseClient as any).rest.headers.Authorization = `Bearer ${jwt}`;
		}
	}
};

// for privy integration
// TODO: what is getSupabase used for? Does it need to replace our user function throughout the app (in user.store.ts)?
const getSupabase = (accessToken: string) => {
	const options: SupabaseClientOptions<'public'> = {};

	if (accessToken) {
		options.global = {
			headers: {
				// This gives Supabase information about the user (wallet) making the request
				Authorization: `Bearer ${accessToken}`
			}
		};
	}

	const supabase = createClient(
		String(import.meta.env.VITE_SUPABASE_URL),
		String(import.meta.env.VITE_SUPABASE_ANON_KEY),
		options
	);

	return supabase;
};

export { getSupabase };

/**
 * Convenience re-exports for typed selections
 *
 */
export const auth = supabaseClient.auth;
export const storage = supabaseClient.storage;

/**
 *
 * @param table Supabase table for profiles
 * @returns
 */
export const profileTable = (table: ProfileTable) => supabaseClient.from(table);

/**
 *
 * @param table Supabase table for posts
 * @returns
 */
export const postTable = (table: PostTable) => supabaseClient.from(table);

/**
 *
 * @param table Supabase table for locations
 * @returns
 */
export const locationTable = (table: LocationTable) => supabaseClient.from(table);

/**
 *
 * @param table Supabase table for chats
 * @returns
 */
export const chatTable = (table: ChatTable) => supabaseClient.from(table);

/**
 *
 * @param table Supabase table for chats
 * @returns
 */
export const commentTable = (table: CommentTable) => supabaseClient.from(table);

/**
 *
 * @param table Supabase table for chats
 * @returns
 */
export const digTable = (table: DigTable) => supabaseClient.from(table);

/**
 *
 * @param table Supabase table for chats
 * @returns
 */
export const notificationTable = (table: NotificationTable) => supabaseClient.from(table);

/**
 *
 * @param table Supabase table for dms
 * @returns
 */
export const dmTable = (table: DmTable) => supabaseClient.from(table);

/**
 *
 * @param table Supabase table for dms
 * @returns
 */
export const messageTable = (table: MessageTable) => supabaseClient.from(table);

/**
 *
 * @param table Supabase table for dms
 * @returns
 */
export const dmMembersTable = (table: DmMembersTable) => supabaseClient.from(table);

/**
 *
 * @param table Supabase table for dms
 * @returns
 */
export const messageStatusTable = (table: MessageStatusTable) => supabaseClient.from(table);

/**
 *
 * @param table Supabase table for feature suggestions
 * @returns
 */
export const featureSuggestionTable = (table: FeatureSuggestionTable) => supabaseClient.from(table);

/**
 *
 * @param table Supabase table for feature votes
 * @returns
 */
export const featureVoteTable = (table: FeatureVoteTable) => supabaseClient.from(table);

/**
 *
 * @param table Supabase table for FLO offers
 * @returns
 */
export const offerTable = (table: OfferTable) => supabaseClient.from(table);

/**
 *
 * @param table Supabase table for FLO offer purchases
 * @returns
 */
export const offerPurchaseTable = (table: OfferPurchaseTable) => supabaseClient.from(table);

/**
 *
 * @param table Supabase table for FLO offer ratings
 * @returns
 */
export const offerRatingTable = (table: OfferRatingTable) => supabaseClient.from(table);

/**
 *
 * @param bucket Supabase storage bucket for avatar images
 * @returns
 */
export const avatarBucket = (bucket: AvatarStorageBucket) => supabaseClient.storage.from(bucket);

/**
 *
 * @param bucket Supabase storage bucket for chat images
 * @returns
 */
export const chatBucket = (bucket: ChatStorageBucket) => supabaseClient.storage.from(bucket);

/**
 *
 * @param bucket Supabase storage bucket for dm images
 * @returns
 */
export const dmBucket = (bucket: DmStorageBucket) => supabaseClient.storage.from(bucket);

/**
 *
 * @param bucket Supabase storage bucket for featured images
 * @returns
 */
export const imageBucket = (bucket: ImageStorageBucket) => supabaseClient.storage.from(bucket);

/**
 *
 * @param bucket Supabase storage bucket for FLO offer images
 * @returns
 */
export const offerBucket = (bucket: OfferStorageBucket) => supabaseClient.storage.from(bucket);

/**
 *
 * @param bucket Supabase storage bucket for map marker images
 * @returns
 */
export const mapIconsBucket = (bucket: MapIconsStorageBucket) =>
	supabaseClient.storage.from(bucket);

/**
 * Ensures custom JWT authentication is properly set before database operations
 * This should be called before any database write operations
 * Note: We use Para for authentication, not Supabase's built-in auth
 */
export const ensureSupabaseAuth = async () => {
	if (browser) {
		// Check if JWT is set
		const jwt = localStorage.getItem('supabase_jwt');

		// If no JWT, try to restore it
		if (!jwt) {
			await restoreSupabaseJWT();
		} else {
			// Validate existing JWT
			if (isJWTExpired(jwt)) {
				localStorage.removeItem('supabase_jwt');
				// Clear auth header (we don't use Supabase auth)
				(supabaseClient as any).rest.headers.Authorization = null;
				return false;
			}
		}

		// For custom JWT system, we don't need to call Supabase's auth endpoints
		// Just ensure the Authorization header is set
		if (jwt && !isJWTExpired(jwt)) {
			(supabaseClient as any).rest.headers.Authorization = `Bearer ${jwt}`;
			return true;
		}

		return false;
	}
	return false;
};

/**
 * Wrapper for database operations that ensures authentication
 * Usage: await withAuth(() => yourDatabaseOperation())
 */
export const withAuth = async <T>(operation: () => Promise<T>): Promise<T> => {
	await ensureSupabaseAuth();
	return operation();
};
